import * as React from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import * as Yup from 'yup';
import { useFormik } from 'formik';

import {
  PopupNames,
  useVerifyTeacherLazyQuery,
  VerifyTeacherQuery,
} from 'core';
import {
  usePopupStateOperations,
  useSortErrors,
  useCreateTeacher,
  usePopupData,
} from 'core/hooks';

import { yupValidation } from 'services/YupValidationService';
import { translateRoleToValue } from 'services/TransformService';
import { PATHS } from 'router/config';

import { ErrorsType, ServerCodes, UserRole } from 'types';

import { ISelectOption } from 'components/atoms';
import { personName } from 'core/utils/personName';
import { TeacherQuickAddPopupComponent } from './TeacherQuickAddPopup';
import { ConfirmPopupComponent } from '../ConfirmPopup/ConfirmPopup';
import { ITeacherAddFieldsValues } from '../TeacherAddPopup/TeacherAddFields';

export const getTeacherQuickAddSchema = (t: TFunction) =>
  Yup.object().shape({
    firstName: yupValidation.firstName(
      `${t('teacherPopup.fields.firstName.labelSecond')}`,
      t,
    ),
    middleName: yupValidation.middleName(
      `${t('teacherPopup.fields.middleName.labelSecond')}`,
      t,
    ),
    lastName: yupValidation.lastName(
      `${t('teacherPopup.fields.lastName.labelSecond')}`,
      t,
    ),
    phone: yupValidation.phone(`${t('teacherPopup.fields.phone.label')}`, t),
    email: yupValidation.email(`${t('teacherPopup.fields.email.label')}`, t),
  });

const buildTeacherInput = (v: ITeacherAddFieldsValues) => ({
  firstName: v.firstName || '',
  lastName: v.lastName || '',
  middleName: v.middleName || undefined,
  email: v.email || '',
  phone: v.phone || '',
  siteDisplay: v.displayOnSite || false,
});

interface IQuickConfirm {
  title: string;
  body: string;
  confirmText: string;
  onConfirm: () => void;
}

/** Преподаватель, которого окно вернуло вызывающему (`onPicked`). */
export interface ITeacherQuickAddResult {
  id: string;
  name: string;
  email: string;
}

/**
 * `popupData` окна: поля формы (предзаполнение) и, по желанию, `onPicked`.
 *
 * `onPicked` — окно открыто ИЗ другого окна (решение владельца 2026-09-22:
 * «Договор с преподавателем» на странице договоров): вместо перехода в
 * карточку преподавателя оно закрывается и отдаёт созданного (или уже
 * существующего) преподавателя вызывающему. Без `onPicked` — как раньше:
 * переход в карточку.
 */
export interface ITeacherQuickAddPopupData extends ITeacherAddFieldsValues {
  onPicked?: (teacher: ITeacherQuickAddResult) => void;
}

const nameOf = (v: ITeacherAddFieldsValues) => personName(v);

/**
 * Standalone («свободный») teacher quick-add (spec 0007 rev.). On submit it
 * first calls `verifyTeacher` to see whether this person already exists — one
 * human can be both a student and a teacher:
 *   - new person        → create straight away (`createTeacher`);
 *   - exists (student…) → confirm, then ADD the teacher role (backend appends
 *     the role, keeping the existing ones);
 *   - already a teacher → offer to open their existing card (no second Teacher).
 * Mirrors the verify→confirm UX of the full add-with-application flow, but skips
 * the employment-type / application steps.
 *
 * The confirmation is rendered LOCALLY (not via the global `confirmPopup`) so
 * this container — and its `useCreateTeacher` hook — stays mounted: switching to
 * a different global popup would unmount it, and the mutation's success/error
 * effects (toast + navigate) would silently never fire.
 */
export const TeacherQuickAddPopup = memo(() => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const popupData = usePopupData(PopupNames.teacherQuickAddPopup);
  // Поля формы — без колбэка: formik (`enableReinitialize`) сравнивает
  // `initialValues` целиком, и функция в них была бы лишним «полем».
  const savedValues = useMemo(
    () =>
      popupData
        ? (Object.fromEntries(
            Object.entries(popupData).filter(([key]) => key !== 'onPicked'),
          ) as ITeacherAddFieldsValues)
        : undefined,
    [popupData],
  );
  // Ref, а не значение из замыкания: `hidePopup` стирает `popupData`, а
  // колбэк нужен уже после этого (и в обработчике `verifyTeacher`).
  const pickedRef = useRef(popupData?.onPicked);
  pickedRef.current = popupData?.onPicked;
  const [disabledButton, setDisabledButton] = useState(true);
  const [confirm, setConfirm] = useState<IQuickConfirm | null>(null);

  const {
    operations: { hidePopup },
  } = usePopupStateOperations();

  // Latest submitted form values — read fresh (via ref) inside the verify
  // onCompleted handler to build the create input, avoiding a stale closure.
  const submittedRef = useRef<ITeacherAddFieldsValues>({});

  const onClose = useCallback(() => {
    hidePopup(PopupNames.teacherQuickAddPopup);
  }, [hidePopup]);

  /** Закрыть окно и отдать преподавателя: вызывающему или его карточке. */
  const finish = useCallback(
    (teacher: ITeacherQuickAddResult) => {
      const pick = pickedRef.current;
      hidePopup(PopupNames.teacherQuickAddPopup);
      if (pick) {
        pick(teacher);
        return;
      }
      history.push(PATHS.teacherDetail.replace(':id', teacher.id));
    },
    [hidePopup, history],
  );

  const onCreated = useCallback(
    (teacherId: string) => {
      const submitted = submittedRef.current;
      finish({
        id: teacherId,
        name: nameOf(submitted),
        email: submitted.email || '',
      });
    },
    [finish],
  );

  const [
    createTeacher,
    { loading: createLoading, errors: serverErrors },
  ] = useCreateTeacher(onCreated);

  const handleVerified = useCallback(
    (data: VerifyTeacherQuery) => {
      const res = data?.verifyTeacher;
      if (!res) return;
      const input = buildTeacherInput(submittedRef.current);

      // Brand-new person → create straight away.
      if (res.code === ServerCodes.success) {
        createTeacher({ teacher: input }, false);
        return;
      }

      // credentials text (RU/PL), mirroring the full add-teacher flow.
      const phone = res.user?.phone || res.teacher?.user?.phone;
      const email = res.user?.email || res.teacher?.user?.email;
      const numberText = phone && `${t('teacherPopup.number')} ${phone}`;
      const emailText = email && `${t('teacherPopup.mail')} ${email}`;
      const joiner = i18n.language === 'ru' ? 'и' : 'i';
      const both =
        numberText && emailText && `${numberText} ${joiner} ${emailText}`;
      const prefix = i18n.language === 'ru' ? 'с' : 'z';
      const credentialsText = `${prefix} ${
        both || numberText || emailText || ''
      }`;

      // Exists as a student / other role → confirm, then add the teacher role.
      // The matched person's name is shown so an admin can catch a mistyped
      // email/phone that resolved to the wrong existing user.
      if (res.code === ServerCodes.userExist) {
        const roles = res.user?.roles
          ?.map((role) => translateRoleToValue(role as UserRole))
          .join(', ');
        const name = personName(res.user);
        setConfirm({
          title: `${t('teacherPopup.teacherIsInSystem')}`,
          body: `${name ? `${name} — ` : ''}${t(
            'teacherPopup.addAnotherAccess',
            { roles, credentialsText },
          )} "${translateRoleToValue(UserRole.TEACHER)}"?`,
          confirmText: `${t('teacherPopup.button.add')}`,
          onConfirm: () => {
            setConfirm(null);
            createTeacher({ teacher: input }, true);
          },
        });
        return;
      }

      // Already a teacher → can't create a second one; offer to open the card
      // (or, opened from another window, to pick this existing teacher).
      if (
        res.code === ServerCodes.teacherExist ||
        res.code === ServerCodes.teacherAgreementActive
      ) {
        const teacherId = res.teacher?.id;
        const teacherFullname = personName(res.teacher?.user);
        const picking = Boolean(pickedRef.current);
        setConfirm({
          title: `${t('teacherPopup.teacherIsInSystem')}`,
          body: `${t(
            picking
              ? 'teacherPopup.pickExistingTeacher'
              : 'teacherPopup.openTeacherCard',
            { teacherFullname },
          )}`,
          confirmText: `${t(
            picking ? 'teacherPopup.button.pick' : 'teacherPopup.button.open',
          )}`,
          onConfirm: () => {
            setConfirm(null);
            if (teacherId) {
              finish({
                id: teacherId,
                name: teacherFullname,
                email: res.teacher?.user?.email || '',
              });
            }
          },
        });
      }
    },
    [createTeacher, finish, t, i18n],
  );

  const [verifyTeacher, { loading: verifyLoading }] = useVerifyTeacherLazyQuery(
    {
      fetchPolicy: 'network-only',
      onCompleted: handleVerified,
    },
  );

  const onSubmitHandler = useCallback(
    (formValues: ITeacherAddFieldsValues) => {
      submittedRef.current = formValues;
      verifyTeacher({
        variables: {
          firstName: formValues.firstName || '',
          middleName: formValues.middleName,
          lastName: formValues.lastName || '',
          email: formValues.email || '',
          phone: formValues.phone || '',
        },
      });
    },
    [verifyTeacher],
  );

  const { values, errors, handleSubmit, setFieldValue } = useFormik({
    initialValues: savedValues || {},
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema: getTeacherQuickAddSchema(t),
    onSubmit: onSubmitHandler,
    enableReinitialize: true,
  });

  const handleChange = useCallback(
    (name: string, value?: string | boolean | ISelectOption<string>[]) => {
      setFieldValue(name, value);
    },
    [setFieldValue],
  );

  useEffect(() => {
    const filled = Boolean(
      values.phone && values.email && values.firstName && values.lastName,
    );
    setDisabledButton(!filled);
  }, [values]);

  const [
    sortErrors,
    { errors: sortedErrors, message },
  ] = useSortErrors<ITeacherAddFieldsValues>();

  useEffect(() => {
    const clientErrors = Object.values(errors).length;
    if (clientErrors) {
      sortErrors(errors as ErrorsType<ITeacherAddFieldsValues>);
      return;
    }

    if (serverErrors) {
      sortErrors(serverErrors);
      return;
    }

    sortErrors(null);
  }, [errors, serverErrors, sortErrors]);

  if (confirm) {
    return (
      <ConfirmPopupComponent
        title={confirm.title}
        confirmText={confirm.confirmText}
        onConfirm={confirm.onConfirm}
        onCancel={() => setConfirm(null)}
        loading={createLoading}
      >
        {confirm.body}
      </ConfirmPopupComponent>
    );
  }

  return (
    <TeacherQuickAddPopupComponent
      values={values}
      errors={sortedErrors}
      errorMessage={message}
      onInputChange={handleChange}
      handleSubmit={handleSubmit}
      onClose={onClose}
      loading={verifyLoading || createLoading}
      disabledButton={disabledButton}
    />
  );
});
